import type { Formatter } from '#client/components/charts';
import type { Metric, MetricUnit } from '#shared/types';
import { signed } from '#client/lib/format';

/**
 * How each kind of number reads.
 *
 * In one place because a chart axis, a KPI tile and a table cell showing the
 * same measure must agree — a tooltip saying "6.5" beside an axis saying
 * "6.5 h" beside a tile saying "7 hours" is three answers to one question.
 */

const RUPEES = new Intl.NumberFormat('en-PK', { maximumFractionDigits: 0 });
const PLAIN = new Intl.NumberFormat('en-PK');

/** Whole rupees. Analytics reads in trends, where paisa is noise. */
export function formatRupees(value: number): string {
  return `Rs ${RUPEES.format(value)}`;
}

export function formatPercent(value: number): string {
  // The wire carries a fraction; a percentage is a rendering decision, so the
  // ×100 happens here and not in six services.
  return `${Math.round(value * 1000) / 10}%`;
}

export function formatHours(value: number): string {
  if (value < 1) return `${Math.round(value * 60)} min`;
  if (value < 48) return `${Math.round(value * 10) / 10} h`;
  return `${Math.round(value / 24)} days`;
}

/**
 * Days of stock cover.
 *
 * Capped in the *display* rather than in the data. A reagent with two hundred
 * years of cover at the current rate is arithmetically true and operationally
 * meaningless, and printing the figure invites somebody to act on it. "1 yr+"
 * says the only thing worth saying: this is not what to worry about.
 */
export function formatCover(value: number | null): string {
  if (value === null) return 'No usage';
  if (value > 365) return '1 yr+';
  return `${value} days`;
}

export function formatterFor(unit: MetricUnit): Formatter {
  switch (unit) {
    case 'money':
      return formatRupees;
    case 'percent':
      return formatPercent;
    case 'hours':
      return formatHours;
    case 'days':
      return (value) => `${Math.round(value)} days`;
    case 'ratio':
      return (value) => String(Math.round(value * 100) / 100);
    default:
      return (value) => PLAIN.format(value);
  }
}

/**
 * A metric's headline text.
 *
 * Four states, four renderings, and none of them is a bare zero:
 *
 *   not knowable  an em dash — the data cannot answer
 *   withheld      an em dash — this role may not see it
 *   zero          the server's `zero_label`, e.g. "Nothing overdue"
 *   a number      formatted for its unit
 *
 * The rule about zero comes from `StatTile`, which has enforced it on the
 * dashboard since it was written: "0" in a large numeral reads as a failure
 * state, and half the time it is the best news on the screen.
 */
export function formatMetricValue({
  value,
  unit,
  known,
  zero_label: zeroLabel,
}: Pick<Metric, 'value' | 'unit' | 'known' | 'zero_label'>): string {
  if (!known || value === null) return '—';
  if (value === 0 && zeroLabel) return zeroLabel;
  return formatterFor(unit)(value);
}

/** A delta, as text. Null when there is nothing to compare against. */
export function formatDelta(
  change: number | null,
  ratio: number | null,
  unit: MetricUnit,
): string | null {
  if (ratio !== null) return signed(ratio, formatPercent(ratio));
  if (change !== null) return signed(change, formatterFor(unit)(change));
  return null;
}
